import React, { useState } from 'react';

import './Team.manage.scss';
import {Subject} from "rxjs";
import {useMutation, useQuery} from "@apollo/client";
import gqlSchemaLibrary from "../../services/graphql.schemas";
import SpinnerLoader from "../../visuals/SpinnerLoader";
import TeamManageItem from "./Team.manage.item";
import TeamMembersDialog from "../dialogs/Team.members.dialog";
import ButtonIcon from "../../forms/buttons/Button.icon";
import Checkbox from "../../forms/inputs/Checkboxk";
import ItemMenu from "../../forms/menu/MenuComponent";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import TeamRenameDialog from "../dialogs/Team.rename.dialog";
import TeamDeleteDialog from "../dialogs/Team.delete.dialog";
import {clearGqlTypename} from "../../utils/clearTypename";
import {PageViewLog} from "../../services/interactions/page.view";
import AuditTrail from "../../services/interactions/audit.trail";


function TeamManage({ event, currentOrg, currentUser, match }) {
    let { teamId } = useParams();
    const {t} = useTranslation(['common','teams','users']);
    const [team, setTeam] = useState(null);
    const [members, setMembers] = useState([]);
    const [sortAscending, setSortAscending] = useState(true);
    const [sorting, setSorting] = useState('name');

    const [searchFocused, changeSearchFocused] = useState(false);
    const [search, changeSearch] = useState('');
    const [searchTerm] = useState(new Subject());
    const [openMembersModal, setOpenMembersModal] = useState(false);
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [hoverMember, setHoverMember] = useState(null);
    const [selectAll, setSelectAll] = useState(false);
    const [openTeamRenameModal, setOpenTeamRenameModal] = useState(false);
    const [openTeamDeleteModal, setOpenTeamDeleteModal] = useState(false);
    const navigate = useNavigate();

    const handleFocus = (bool) => {
        return () => {
            AuditTrail.sendLog(event, {tag: 'input', event: 'onSearch', action: 'input search members', focus: bool, value: search});
            changeSearchFocused(bool);
        }
    }

    const handleSearch = (e) => {
        const value = e.target.value;
        changeSearch(value)
        searchTerm.next(value);
    }

    const loadMembers = (team) => {
        const _members = [];
        for (const m of team.users) {
            const name = m.user.bio.lastname + ', ' + m.user.bio.firstname;
            _members.push({
                _id: m._id,
                name: name,
                role: m.role,
                email: m.user.email,
                type: "User"
            });
        }
        for (const t of team.teams) {
            _members.push({
                _id: t._id,
                name: t.name,
                role: "",
                email: "",
                type: "Team"
            });
        }
        setMembers(_members);
    }

    useQuery(gqlSchemaLibrary.teams.queries.getTeam, {
        variables: {
            teamId: teamId
        },
        onCompleted: ({ getTeam }) => {
            setTeam(clearGqlTypename(getTeam));
            loadMembers(getTeam);
        },
        fetchPolicy: 'cache-and-network'
    })

    const [editMembersTeam] = useMutation(gqlSchemaLibrary.teams.mutation.editMembersTeam, {
        onCompleted: ({ editTeam }) => {
            setSelectedMembers([]);
            console.log('team created: ', editTeam);
        },
        onError: (e) => {
            console.error('Update Error: ', e);
        }
    });

    if (!team) {
        return <SpinnerLoader/>
    }

    const sortList = (key) => {
        return () => {
            AuditTrail.sendLog(event, {tag: 'button', event: 'onClick', action: 'button toggle sort', sort: key, sort_ascending: !sortAscending});
            setSorting(key);
            setSortAscending(!sortAscending);
        }
    }

    const filteredMembers = members.filter((m => {
        if (!search) {
            return true;
        }
        return m.name.toLowerCase().includes(search.toLowerCase());
    })).sort((a, b) => {
        if(!sorting){
            return true;
        }
        if(sorting === 'name'){
            if(sortAscending){
                return a.name.localeCompare(b.name);
            }else{
                return b.name.localeCompare(a.name);
            }
        }
        return true;
    });

    const onOpenModal = () => {
        setOpenMembersModal(true);
    }

    const onCloseModal = (team, data) => {
        if (data) {
            const _team = Object.assign({}, team);
            _team.users = data.users;
            _team.teams = data.teams;
            setTeam(_team);
            setMembers(_team.users.concat(_team.teams));
        }
        setOpenMembersModal(false);
    }

    const deleteMembers = (members) => {
        setMembers(Object.assign([], members));
        const users = members.filter((m) => m.type === 'User').map((u) => u._id);
        const teams = members.filter((m) => m.type === 'Team').map((u) => u._id);
        const _team = Object.assign({}, team);
        _team.users = members.filter((m) => m.type === 'User');
        _team.teams = members.filter((m) => m.type === 'Team');
        setTeam(_team);
        AuditTrail.sendLog(event, {tag: 'button', event: 'onClick', action: 'click option delete members', user_ids: users, team_ids: teams });
        editMembersTeam({
            variables: {
                teamMembers: {
                    _id: team._id,
                    users: users,
                    teams: teams
                }
            }
        });
    }

    const onDeleteMember = (member) => {
        const index = members.indexOf(member);
        if (index > -1) {
            members.splice(index, 1);
        }
        deleteMembers(members);
    }

    const onChange = (member) => {
        const _member = selectedMembers.find((t) => t._id === member._id);
        const index = selectedMembers.indexOf(_member);
        const checked = index > -1;
        AuditTrail.sendLog(event, {tag: 'checkbox', event: 'onCheck', action: 'button selected member', team_id: teamId, member_id: member._id, member_type: member.type, check: !checked});
        if (checked) {
            selectedMembers.splice(index, 1);
        } else {
            selectedMembers.push(member);
        }
        if (!selectedMembers.length) {
            setSelectAll(false);
        }
        setSelectedMembers(Object.assign([], selectedMembers));
    }

    const onSelectAll = () => {
        const status = !selectAll;
        if (status) {
            setSelectedMembers(Object.assign([], members));
        } else {
            setSelectedMembers([]);
        }
        setSelectAll(status);
    }

    const onDeleteMembers = () => {
        for (const m of selectedMembers) {
            const index = members.indexOf(m);
            if (index > -1) {
                members.splice(index, 1);
            }
        }
        deleteMembers(members);
    }

    const onClickRenameTeam = () => {
        setOpenTeamRenameModal(true);
    }

    const onBack = () => {
        AuditTrail.sendLog(event, {tag: 'button', event: 'onClick', action: 'button back'});
        navigate(-1);
    }

    const closeRenameModal = (name) => {
        if (name) {
            team.name = name;
            setTeam(team);
        }
        setOpenTeamRenameModal(false);
    }

    const onClickDeleteTeam = () => {
        setOpenTeamDeleteModal(true);
    }

    const closeDeleteModal = (team) => {
        if (team) {
            navigate(`/teams`);
        } else {
            setOpenTeamDeleteModal(false);
        }
    }

    return (
        <div id="TeamManage">
            <div className="filter">
                <p>
                </p>
                <div className={`search ${searchFocused ? 'focused' : ''}`}>
                    <input value={search} placeholder={t('search')} onChange={handleSearch} onFocus={handleFocus(true)} onBlur={handleFocus(false)} />
                    <i className="material-icons">
                        search
                    </i>
                </div>
                <div className="addAction">
                    <p className="_counter">
                        {filteredMembers.length} {t('teams:member')}{filteredMembers.length === 1? '' : 's'}
                    </p>
                </div>
            </div>
            <div className="teamContent">
                <div className="sideA">
                    <p className="title">{team.name}</p>
                    <p className="text">{team.description}</p>
                    {
                        currentUser.userRoles.userPatients.edit?
                            <div className="floatAction" onClick={onOpenModal}>
                                <ButtonIcon>
                                    <span className="material-icons">
                                        add
                                    </span>
                                </ButtonIcon>
                            </div> : ''
                    }
                    {
                        currentUser.userRoles.userPatients.edit?
                            <div className="containerButtons">
                                <button className="primary gray" onClick={onClickRenameTeam}>
                                    {t('rename')}<br/> {t('teams:team')}
                                </button>
                                <button className="primary gray" onClick={onClickDeleteTeam}>
                                    {t('delete')}<br/> {t('teams:team')}
                                </button>
                            </div> : ''
                    }
                </div>
                <div className="sideB">
                    <p className="title">{t('teams:members')}</p>
                    <div className="membersList">
                        <div className="headers">
                            <div>
                                {
                                    currentUser.userRoles.userPatients.edit? <Checkbox bool={selectAll} onChange={onSelectAll}/> : ''
                                }
                            </div>
                            <div className="selectable sortable">
                                <p onClick={sortList('name')}>
                                    {t('name')}
                                </p>
                                <div className="_updown" onClick={sortList('name')}>
                                    <span className="_up material-icons">expand_less</span>
                                    <span className="_down material-icons">expand_more</span>
                                </div>
                            </div>
                            <div className="selectable">
                                <p>
                                    {t('users:email')}
                                </p>
                            </div>
                            <div className="selectable">
                                <p>
                                    {t('type')}
                                </p>
                            </div>
                            <div>
                                {
                                    currentUser.userRoles.userPatients.edit && selectedMembers.length?
                                        <ItemMenu actionWillRedirect={false} fixed={true} label={'Member Options'} event={event} eventParams={{team_id: teamId}}>
                                            <>
                                                <div className="_option" onClick={onDeleteMembers}>
                                                    <p>{t('teams:delete-members')}</p>
                                                </div>
                                            </>
                                        </ItemMenu> : ''
                                }
                            </div>
                        </div>
                        {
                            filteredMembers.map((member, index) => (
                                <TeamManageItem key={index} currentUser={currentUser} member={member} selectedMembers={selectedMembers} hoverMember={hoverMember} onHover={setHoverMember} onDelete={onDeleteMember} onChange={onChange} />
                            ))
                        }
                        {
                            filteredMembers.length === 0? (<p className="empty">{t('teams:this-team-has-no-members')}</p>) : ''
                        }
                    </div>
                    <div className="containerButtons">
                        <button className="primary gray" onClick={onBack}>
                            {t('back')}
                        </button>
                    </div>
                </div>
            </div>
            <TeamRenameDialog event={event} team={team} openModal={openTeamRenameModal} closeModal={closeRenameModal} />
            <TeamDeleteDialog event={event} team={team} teams={[]} redirect={true} openModal={openTeamDeleteModal} closeModal={closeDeleteModal} />
            {
                openMembersModal? <TeamMembersDialog event={event} team={team} openModal={openMembersModal} closeModal={onCloseModal} /> : ''
            }
        </div>
    )
}

export default PageViewLog(TeamManage);
